const firstNameOnly = (value) => {
  const clean = String(value || '').trim();
  return clean ? clean.split(/\s+/)[0] : 'Cuidador';
};

import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Star, Shield, ShieldCheck, MapPin, Phone, MessageSquare, Heart, Lock, Camera, X, Dog, CalendarPlus, Crown, Home, PawPrint, Briefcase, Clock, UserCircle, Send, CheckCircle, Loader2 } from 'lucide-react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api, { API_BASE } from '@/lib/api';
import BookingForm from '@/components/BookingForm';
import SubscriptionCard from '@/components/SubscriptionCard';
import ServiceDatesSelector from '@/components/ServiceDatesSelector';
import CareRequestsClient from '@/components/CareRequestsClient';
import { PetForm } from './PetSetup';
import { isUcanApp, isApplePlatform } from '@/lib/ucanApp';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const LIBRARIES = ['places'];

class MapErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) {
    console.log('Map error:', error);
  }
  render() {
    if (this.state.hasError) {
      return <div className="h-[250px] bg-gray-100 flex items-center justify-center text-gray-400 text-sm">Mapa no disponible</div>;
    }
    return this.props.children;
  }
}

const SafeMap = ({ lat, lng }) => {
  if (!lat || !lng || !window.google?.maps) {
    return <div className="h-[250px] bg-gray-100 flex items-center justify-center text-gray-400 text-sm">Mapa no disponible</div>;
  }
  
  return (
    <MapErrorBoundary>
      <GoogleMap
        center={{ lat, lng }}
        zoom={15}
        mapContainerStyle={{ width: '100%', height: '250px' }}
        options={{ disableDefaultUI: true, zoomControl: true, mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}
      >
        <Marker position={{ lat, lng }} />
      </GoogleMap>
    </MapErrorBoundary>
  );
};
const PET_SIZE_LABELS = { pequeno: 'Pequeno', mediano: 'Mediano', grande: 'Grande' };
const API_URL = process.env.REACT_APP_BACKEND_URL;

const getPhotoUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_URL}${path}`;
};

export default function ProviderProfile() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const profileRedirect = `${location.pathname}${location.search}`;
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewPhotos, setReviewPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [clientPets, setClientPets] = useState([]);
  const [sendingContactRequest, setSendingContactRequest] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [showDirectContactModal, setShowDirectContactModal] = useState(false);
  const [showFreeContactModal, setShowFreeContactModal] = useState(false);
  const [showNewClientPetModal, setShowNewClientPetModal] = useState(false);
  const [clientPremiumPlan, setClientPremiumPlan] = useState(null);
  const [startingPremiumCheckout, setStartingPremiumCheckout] = useState(false);
  const [directContactPetId, setDirectContactPetId] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const fileInputRef = useRef(null);
  const contactBoxRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: LIBRARIES
  });

  const isClient = user?.role === 'client';
  const isPremiumClient = !!user?.is_subscribed;

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('newClientPet') === '1') {
      setShowNewClientPetModal(true);
    }
  }, [location.search]);

  useEffect(() => {
    loadProvider();
    loadUser();
    loadFavoriteStatus();
  }, [providerId]);

  const loadProvider = async () => {
    try {
      const res = await api.get(`/providers/${providerId}`);
      setProvider(res.data);
    } catch (e) {
      toast.error('Cuidador no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const loadUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {
      localStorage.setItem('ucan_after_login_redirect', profileRedirect);
    }
  };

  const loadFavoriteStatus = async () => {
    try {
      const res = await api.get('/favorites');
      const list = Array.isArray(res.data) ? res.data : [];
      setIsFavorite(list.some(f => f.provider_id === providerId));
    } catch {
      setIsFavorite(false);
    }
  };


  const loadClientPetsForRequest = async () => {
    try {
      const res = await api.get('/pets');
      setClientPets(Array.isArray(res.data) ? res.data : []);
    } catch {
      setClientPets([]);
    }
  };

  const openRequestForm = async () => {
    if (!user) {
      localStorage.setItem('ucan_after_login_redirect', profileRedirect);
      navigate('/login');
      return;
    }

    await loadClientPetsForRequest();
    setShowRequestForm(true);
  };

  const openFreeContactModal = async () => {
    if (!user) {
      localStorage.setItem('ucan_after_login_redirect', profileRedirect);
      navigate('/login');
      return;
    }

    setShowFreeContactModal(true);

    if (!clientPremiumPlan) {
      try {
        const res = await api.get('/subscription/plans?role=client');
        const plans = Array.isArray(res.data) ? res.data : [];
        const activePlan = plans.find(p => p.active) || plans[0] || null;
        setClientPremiumPlan(activePlan);
      } catch (error) {
        console.error('Error loading client premium plan:', error);
      }
    }
  };

  const startPremiumCheckout = async () => {
    if (!clientPremiumPlan || startingPremiumCheckout) return;

    if (isUcanApp()) {
      window.ReactNativeWebView?.postMessage(JSON.stringify({
        type: 'APPLE_SUBSCRIPTION',
        plan_id: clientPremiumPlan.plan_id,
        user_type: 'client',
        promo_code: null
      }));
      return;
    }

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'APPLE_SUBSCRIPTION',
        plan_id: clientPremiumPlan.plan_id,
        user_type: 'client',
        promo_code: null
      }));
      return;
    }

    // En cualquier navegador Apple nunca usamos Mercado Pago.
    if (isApplePlatform()) {
      toast.info('Suscríbete con Apple desde la app U-CAN para iOS.');
      return;
    }

    setStartingPremiumCheckout(true);

    try {
      const response = await api.post('/subscription/create-payment', {
        plan_id: clientPremiumPlan.plan_id
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.sandbox_url) {
        window.location.href = response.data.sandbox_url;
      } else {
        toast.error('Error al crear el pago');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar suscripción');
    } finally {
      setStartingPremiumCheckout(false);
    }
  };

  const continueWithFreeRequest = async () => {
    setShowFreeContactModal(false);
    await openRequestForm();
  };


  const openPremiumContactBox = () => {
    if (!user) {
      localStorage.setItem('ucan_after_login_redirect', profileRedirect);
      navigate('/login');
      return;
    }

    setTimeout(() => {
      contactBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const textarea = document.querySelector('[data-testid="contact-message-input"]');
      textarea?.focus();
    }, 100);
  };

  const openDirectContactModal = async () => {
    if (!user) {
      localStorage.setItem('ucan_after_login_redirect', profileRedirect);
      navigate('/login');
      return;
    }
    await loadClientPetsForRequest();
    setShowDirectContactModal(true);
  };

  const sendDirectContactRequest = async () => {
    if (!user) {
      localStorage.setItem('ucan_after_login_redirect', profileRedirect);
      navigate('/login');
      return;
    }

    setSendingContactRequest(true);
    try {
      await api.post('/contact-requests', {
        provider_user_id: provider.user_id,
        message: contactMessage || 'Hola, me gustaria contactarte para un servicio.',
        pet_id: directContactPetId
      });
      toast.success('Solicitud enviada! Te notificaremos cuando responda.');
      setShowDirectContactModal(false);
      setContactMessage('');
      setDirectContactPetId('');
      loadProvider();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setSendingContactRequest(false);
    }
  };

  const toggleFavorite = async () => {
    if (favoriteLoading) return;

    if (!user) {
      toast.error('Debes iniciar sesión para agregar favoritos');
      localStorage.setItem('ucan_after_login_redirect', profileRedirect);
      navigate('/login');
      return;
    }

    if (user.role !== 'client') {
      toast.error('Solo los clientes pueden agregar favoritos');
      return;
    }

    try {
      setFavoriteLoading(true);

      if (isFavorite) {
        await api.delete(`/favorites/${providerId}`);
        window.dispatchEvent(new Event('ucan:favorites-updated'));
        setIsFavorite(false);
        toast.success('Eliminado de favoritos');
      } else {
        await api.post(`/favorites/${providerId}`);
        window.dispatchEvent(new Event('ucan:favorites-updated'));
        setIsFavorite(true);
        toast.success('Agregado a favoritos');
      }
    } catch (e) {
      toast.error('No se pudo actualizar favoritos');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (reviewPhotos.length + files.length > 4) {
      toast.error('Maximo 4 fotos');
      return;
    }
    setUploading(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/reviews/upload-photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setReviewPhotos(prev => [...prev, res.data.url]);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Error al subir foto');
      }
    }
    setUploading(false);
  };

  const submitReview = async () => {
    if (!reviewText.trim()) { toast.error('Escribe un comentario'); return; }
    setSubmitting(true);
    try {
      await api.post('/reviews', {
        provider_id: providerId,
        rating: reviewRating,
        comment: reviewText,
        photos: reviewPhotos
      });
      toast.success('Calificacion guardada. Se publicara cuando ambos califiquen o en 7 dias.');
      setReviewText('');
      setReviewRating(5);
      setReviewPhotos([]);
      loadProvider();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar reseña');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-16 h-16 border-4 border-[#E6202E] border-t-transparent rounded-full" /></div>;
  if (!provider) return <div className="min-h-screen flex items-center justify-center text-gray-500">Cuidador no encontrado</div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="provider-profile">
      {/* Hero */}
      <div className="relative h-[208px] bg-gradient-to-r from-[#E6202E] to-[#D31522]">
        {provider.photos?.[0] && (
          <img src={provider.photos[0]} alt="" className="w-full h-full object-cover opacity-30" />
        )}
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto w-full px-4 pb-8">
            <div className="flex items-end gap-4">
              <div className="w-36 h-36 rounded-2xl bg-white shadow-xl flex items-center justify-center overflow-hidden shrink-0">
                {provider.profile_photo ? (
                  <img src={getPhotoUrl(provider.profile_photo)} alt="" className="w-full h-full object-cover" />
                ) : provider.photos?.[0] ? (
                  <img src={provider.photos[0]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-[#E6202E] fill-[#E6202E]">{firstNameOnly(provider.business_name)?.[0]}</span>
                )}
              </div>
              <div className="text-white pb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-3xl font-bold" data-testid="provider-name">{firstNameOnly(provider.business_name)}</h1>

                  <button
                    type="button"
                    onClick={toggleFavorite}
                    disabled={favoriteLoading}
                    className={`inline-flex items-center justify-center w-11 h-11 rounded-full shadow-md transition-all ${
                      isFavorite
                        ? 'bg-white text-[#E6202E] fill-[#E6202E] hover:bg-red-50'
                        : 'bg-white text-gray-700 hover:text-[#E6202E] fill-[#E6202E]'
                    }`}
                    title={isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                  >
                    <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                  </button>
                  {provider.is_subscribed && (
                    <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-sm font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md" data-testid="premium-badge-profile">
                      <Crown className="w-4 h-4" />Premium
                    </span>
                  )}
                  {provider.verified && <ShieldCheck className="w-6 h-6 text-sky-500 fill-sky-500" />}
                  {provider.full_name_hidden && (
                    <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Lock className="w-3 h-3" />Nombre parcial
                    </span>
                  )}
                </div>
                <p className="opacity-80">{provider.comuna}</p>
              </div>
            </div>
          </div>
        </div>
      </div>


      {showRequestForm && (
        <div data-testid="provider-request-modal">
          <style>{`
            [data-testid="provider-request-modal"] [data-testid="care-requests-section"] {
              background: transparent !important;
              border: 0 !important;
              box-shadow: none !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            [data-testid="provider-request-modal"] [data-testid="care-requests-section"] > :not(.fixed) {
              display: none !important;
            }
          `}</style>
          <CareRequestsClient
            pets={clientPets}
            forceOpenForm={true}
            hideHeaderButton={true}
            onRequestCreated={() => setShowRequestForm(false)}
          />
        </div>
      )}

      {showFreeContactModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          data-testid="free-contact-options-modal"
          onClick={() => setShowFreeContactModal(false)}
        >
          <div
            className="relative w-full max-w-lg my-8 bg-white rounded-3xl shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowFreeContactModal(false)}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="px-6 sm:px-8 pt-8 pb-6">
              <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center mb-5">
                <Crown className="w-7 h-7 text-green-600" />
              </div>

              <h2 className="font-montserrat text-2xl sm:text-3xl font-bold text-gray-900 pr-8">
                Contacta a {firstNameOnly(provider?.business_name)}
              </h2>

              <p className="text-gray-600 mt-3 leading-relaxed">
                Hazte Premium y envía tu solicitud directamente a este cuidador.
              </p>

              <div className="mt-6 bg-green-50 border border-green-200 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                    <Crown className="w-5 h-5 text-green-600" />
                  </div>

                  <div className="flex-1">
                    <p className="font-bold text-gray-900 text-lg">
                      U-CAN Premium
                    </p>

                    {clientPremiumPlan ? (
                      <p className="text-2xl font-bold text-green-600 mt-1">
                        ${clientPremiumPlan.price_clp?.toLocaleString('es-CL')}/mes
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 mt-1">
                        Cargando plan...
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                    <span>Contacta cuidadores directamente</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                    <span>Envía solicitudes ilimitadas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                    <span>Acceso prioritario a cuidadores destacados</span>
                  </div>
                </div>

                <Button
                  onClick={startPremiumCheckout}
                  disabled={!clientPremiumPlan || startingPremiumCheckout}
                  className="w-full mt-5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl py-6 text-base"
                  data-testid="free-modal-premium-btn"
                >
                  {startingPremiumCheckout ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Procesando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Crown className="w-5 h-5" />
                      Hazte Premium y contacta ahora
                    </span>
                  )}
                </Button>
              </div>
            </div>

            <div className="px-6 sm:px-8 pb-8">
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                    <Send className="w-5 h-5 text-[#E6202E]" />
                  </div>

                  <div className="flex-1">
                    <p className="font-bold text-gray-900 text-lg">
                      ¿Prefieres continuar gratis?
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Publica una solicitud abierta y recibe propuestas de cuidadores disponibles.
                    </p>
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#E6202E] shrink-0" />
                    <span>Publica tu solicitud sin costo</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#E6202E] shrink-0" />
                    <span>Recibe propuestas de cuidadores interesados</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#E6202E] shrink-0" />
                    <span>Elige la propuesta que más te acomode</span>
                  </div>
                </div>

                <Button
                  onClick={continueWithFreeRequest}
                  className="w-full mt-5 bg-[#E6202E] hover:bg-[#D31522] text-white font-bold rounded-xl py-6 text-base"
                  data-testid="free-modal-publish-request-btn"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-5 h-5" />
                    Publicar solicitud gratis
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNewClientPetModal && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
          data-testid="new-client-pet-modal"
        >
          <div className="relative w-full max-w-lg my-12 bg-white rounded-2xl shadow-xl border p-6">
            <div className="mb-5">
              <h2 className="font-montserrat text-2xl font-bold text-gray-900 mb-2">
                Agrega tu mascota
              </h2>

              <p className="text-sm text-gray-600">
                Registra tu mascota para continuar usando U-CAN y contactar cuidadores.
              </p>
            </div>

            <PetForm
              onSaved={async (createdPet) => {
                if (createdPet) {
                  setClientPets(prev => [...prev, createdPet]);
                } else {
                  await loadClientPetsForRequest();
                }

                setShowNewClientPetModal(false);

                const params = new URLSearchParams(location.search);
                params.delete('newClientPet');

                const cleanSearch = params.toString();
                navigate(
                  `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`,
                  { replace: true }
                );

                toast.success('Mascota registrada correctamente');
              }}
            />
          </div>
        </div>
      )}

      {showDirectContactModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="direct-contact-modal">
          <div className="relative w-full max-w-xl my-12 bg-white rounded-2xl shadow-xl border p-6">
            <button
              type="button"
              onClick={() => setShowDirectContactModal(false)}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white shadow border text-gray-500 hover:text-[#E6202E] text-2xl leading-none"
              aria-label="Cerrar"
            >
              ×
            </button>

            <div className="mb-5 pr-10">
              <h2 className="font-montserrat text-2xl font-bold text-gray-900 mb-2">
                Enviar solicitud
              </h2>
              <p className="text-sm text-gray-600">
                Cuéntale al cuidador qué necesitas, fechas, horarios y detalles del servicio.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Mascota</label>
              <select
                value={directContactPetId}
                onChange={e => setDirectContactPetId(e.target.value)}
                className="w-full border rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                data-testid="direct-contact-pet-select"
              >
                <option value="">Selecciona mascota</option>
                {clientPets.map(pet => (
                  <option key={pet.pet_id} value={pet.pet_id}>
                    {pet.name} {pet.species ? `(${pet.species})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <textarea
              value={contactMessage}
              onChange={e => setContactMessage(e.target.value)}
              placeholder="Ej: Hola, necesito cuidado para mi mascota este sábado desde las 10:00 hasta las 18:00..."
              className="w-full border rounded-xl p-3 text-sm min-h-[130px] focus:outline-none focus:ring-2 focus:ring-green-600 resize-none"
              data-testid="direct-contact-message-input"
            />

            <Button
              onClick={sendDirectContactRequest}
              disabled={sendingContactRequest}
              className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl py-3"
              data-testid="send-direct-contact-request-btn"
            >
              {sendingContactRequest ? 'Enviando...' : 'Enviar solicitud'}
            </Button>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Rating */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className={`w-5 h-5 ${s <= Math.round(provider.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                  ))}
                </div>
                <span className="text-base font-semibold" data-testid="provider-rating">{provider.rating?.toFixed(1) || 'Sin rating'}</span>
                <span className="text-gray-500">({provider.total_reviews || 0} reseñas)</span>
              </div>
            </div>

            {/* Description */}
            {provider.description && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-bold mb-3">Sobre mi</h2>
                <p className="text-gray-700 leading-relaxed">{provider.description}</p>
              </div>
            )}

            {/* Personal Info (Más Datos) */}
            {provider.personal_info && Object.values(provider.personal_info).some(v => v && v !== '' && v !== false && !(Array.isArray(v) && v.length === 0)) && (
              <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="provider-personal-info">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <UserCircle className="w-5 h-5 text-[#E6202E] fill-[#E6202E]" />
                  Más Sobre el Cuidador
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {provider.personal_info.housing_type && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <Home className="w-5 h-5 text-[#E6202E] fill-[#E6202E] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Vivienda</p>
                        <p className="text-sm text-gray-800 capitalize">{provider.personal_info.housing_type}</p>
                      </div>
                    </div>
                  )}
                  {provider.personal_info.has_yard && (
                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                      <MapPin className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Patio / Jardín</p>
                        <p className="text-sm text-gray-800">{provider.personal_info.yard_description || 'Sí, tiene patio'}</p>
                      </div>
                    </div>
                  )}
                  {provider.personal_info.has_own_pets && (
                    <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl">
                      <PawPrint className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Mascotas Propias</p>
                        <p className="text-sm text-gray-800">{provider.personal_info.own_pets_description || 'Sí, tiene mascotas'}</p>
                      </div>
                    </div>
                  )}
                  {provider.personal_info.daily_availability && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <Clock className="w-5 h-5 text-[#E6202E] fill-[#E6202E] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Disponibilidad</p>
                        <p className="text-sm text-gray-800">{provider.personal_info.daily_availability}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Yard Photos */}
                {provider.personal_info.yard_photos?.length > 0 && (
                  <div className="mt-4" data-testid="yard-photos-public">
                    <p className="text-xs text-gray-500 font-medium mb-2 flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-green-600" /> Fotos del patio</p>
                    <div className="flex gap-2">
                      {provider.personal_info.yard_photos.map((photo, i) => (
                        <div key={photo.photo_id} className="w-28 h-28 rounded-xl overflow-hidden bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => window.open(getPhotoUrl(photo.url), '_blank')}>
                          <img src={getPhotoUrl(photo.thumbnail_url || photo.url)} alt={`Patio ${i+1}`} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pets Photos */}
                {provider.personal_info.pets_photos?.length > 0 && (
                  <div className="mt-4" data-testid="pets-photos-public">
                    <p className="text-xs text-gray-500 font-medium mb-2 flex items-center gap-1"><PawPrint className="w-3.5 h-3.5 text-blue-600" /> Mascotas del cuidador</p>
                    <div className="flex gap-2">
                      {provider.personal_info.pets_photos.map((photo, i) => (
                        <div key={photo.photo_id} className="w-28 h-28 rounded-xl overflow-hidden bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => window.open(getPhotoUrl(photo.url), '_blank')}>
                          <img src={getPhotoUrl(photo.thumbnail_url || photo.url)} alt={`Mascota ${i+1}`} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {provider.personal_info.animal_experience && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Briefcase className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Experiencia con Animales</p>
                        <p className="text-sm text-gray-800">{provider.personal_info.animal_experience}</p>
                      </div>
                    </div>
                  </div>
                )}
                {provider.personal_info.additional_info && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                    <p className="text-xs text-gray-500 font-medium mb-1">Información Adicional</p>
                    <p className="text-sm text-gray-800">{provider.personal_info.additional_info}</p>
                  </div>
                )}
              </div>
            )}

            {/* Photo Gallery */}
            {provider.gallery?.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="provider-gallery-public">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-[#E6202E] fill-[#E6202E]" />
                  Galería
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {provider.gallery.map((photo, index) => (
                    <div 
                      key={photo.photo_id} 
                      className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => window.open(getPhotoUrl(photo.url), '_blank')}
                    >
                      <img
                        src={getPhotoUrl(photo.thumbnail_url || photo.url)}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Services */}
            {provider.services?.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-bold mb-4">Servicios</h2>
                {provider.services.map((s, i) => (
                  <div
                    key={i}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (isPremiumClient) {
                        openDirectContactModal();
                      } else {
                        openFreeContactModal();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (isPremiumClient) {
                          openDirectContactModal();
                        } else {
                          openFreeContactModal();
                        }
                      }
                    }}
                    className="flex justify-between items-center p-4 bg-gray-50 rounded-xl mb-3 cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div>
                      <span className="capitalize font-semibold text-gray-900">{s.service_type}</span>
                      {s.description && <p className="text-sm text-gray-500 mt-0.5">{s.description}</p>}
                      {s.pet_sizes?.length > 0 && (
                        <div className="flex items-center gap-1 mt-2">
                          <Dog className="w-4 h-4 text-gray-400" />
                          {s.pet_sizes.map((size, j) => (
                            <span key={j} className="px-2 py-0.5 bg-white border border-gray-200 text-gray-600 text-xs rounded-full capitalize">
                              {PET_SIZE_LABELS[size] || size}
                            </span>
                          ))}
                        </div>
                      )}
                      {s.rules && <p className="text-xs text-gray-400 mt-1">{s.rules}</p>}
                    </div>
                    <div className="text-right">
                      <span className="text-[#E6202E] fill-[#E6202E] font-bold text-lg">Desde ${s.price_from?.toLocaleString('es-CL')}</span>
                      {s.availability && <p className="text-xs text-gray-400">{s.availability}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Service Zones - Comunas and Walking Zones */}
            {(provider.coverage_radius_km || provider.service_comunas?.length > 0 || provider.walking_zones?.length > 0) && (
              <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="provider-service-zones">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#E6202E] fill-[#E6202E]" />
                  Zona de Servicio
                </h2>
                
                {provider.coverage_radius_km && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-xl">
                    <p className="text-sm text-blue-800">
                      <strong>Radio de cobertura:</strong> {provider.coverage_radius_km} km desde su ubicación
                    </p>
                  </div>
                )}
                
                {provider.service_comunas?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Comunas donde ofrece servicio:</h3>
                    <div className="flex flex-wrap gap-2">
                      {provider.service_comunas.map(comuna => (
                        <span 
                          key={comuna}
                          className="px-3 py-1.5 bg-gray-100 text-gray-800 rounded-full text-sm"
                        >
                          {comuna}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {provider.walking_zones?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Zonas de paseo favoritas:</h3>
                    <div className="flex flex-wrap gap-2">
                      {provider.walking_zones.map(zone => (
                        <span 
                          key={zone}
                          className="px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm"
                        >
                          {zone}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reviews */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold mb-4">Reseñas</h2>

              {/* Write Review */}
              {user && (
                <div className="mb-6 p-4 bg-gray-50 rounded-xl" data-testid="review-form">
                  <div className="flex gap-1 mb-3">
                    {[1,2,3,4,5].map(s => (
                      <button key={s} onClick={() => setReviewRating(s)}>
                        <Star className={`w-6 h-6 ${s <= reviewRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={reviewText}
                    onChange={e => setReviewText(e.target.value)}
                    placeholder="Cuenta tu experiencia con este cuidador..."
                    className="w-full border rounded-xl p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#E6202E] mb-3"
                    data-testid="review-text-input"
                  />
                  {/* Photo Upload */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {reviewPhotos.map((url, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => setReviewPhotos(prev => prev.filter((_, j) => j !== i))}
                          className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {reviewPhotos.length < 4 && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-[#E6202E] hover:text-[#E6202E] fill-[#E6202E] transition-colors"
                        data-testid="upload-photo-button"
                      >
                        <Camera className="w-5 h-5" />
                        <span className="text-[10px] mt-0.5">{uploading ? 'Subiendo...' : 'Foto'}</span>
                      </button>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                  </div>
                  <Button
                    onClick={submitReview}
                    disabled={submitting}
                    className="w-full bg-[#E6202E] hover:bg-[#D31522] text-white"
                    data-testid="submit-review-button"
                  >
                    {submitting ? 'Enviando...' : 'Publicar Resena'}
                  </Button>
                </div>
              )}

              {/* Review List */}
              {provider.reviews?.length > 0 ? (
                <div className="space-y-4">
                  {provider.reviews.map((r, i) => (
                    <div key={i} className="border-b last:border-0 pb-4 last:pb-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
                          {r.user_picture ? <img src={r.user_picture} alt="" className="w-full h-full object-cover" /> : <span className="text-sm font-bold">{r.user_name?.[0]}</span>}
                        </div>
                        <span className="font-medium text-sm">{r.user_name}</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />)}
                        </div>
                      </div>
                      {r.comment && <p className="text-sm text-gray-700">{r.comment}</p>}
                      {r.photos?.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {r.photos.map((url, j) => (
                            <img key={j} src={url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-20 h-20 rounded-lg object-cover border" />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">Sin reseñas aún</p>
              )}
            </div>
          </div>

          {/* Sidebar - Contact */}
          <div className="space-y-6">

            {!user && (
              <div
                className="bg-white rounded-2xl p-5 shadow-sm border mb-4"
                data-testid="profile-login-button-card"
              >
                <p className="text-sm text-gray-500 mb-3 text-center">
                  Inicia sesión para contactar a este cuidador o publicar una solicitud y recibir propuestas.
                </p>

                <Button
                  onClick={() => {
                    localStorage.setItem('ucan_after_login_redirect', profileRedirect);
                    navigate('/login');
                  }}
                  className="w-full font-semibold rounded-xl py-6 text-xl text-white bg-[#E6202E] hover:bg-[#D31522]"
                >
                  Iniciar sesión
                </Button>
              </div>
            )}

            {isClient && (
              <div className="bg-white rounded-2xl p-5 shadow-sm border mb-4" data-testid="profile-request-button-card">

                <p className="text-sm text-gray-500 mb-3 text-center">
                  {isPremiumClient
                    ? 'Esta solicitud será enviada únicamente a este cuidador.'
                    : 'Contacta directamente o publica una solicitud para recibir propuestas.'}
                </p>

                <Button
                  onClick={isPremiumClient ? openDirectContactModal : openFreeContactModal}
                  disabled={sendingContactRequest}
                  className={`w-full font-semibold rounded-xl py-6 text-xl text-white ${
                    isPremiumClient
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-[#E6202E] hover:bg-[#D31522]'
                  }`}
                >
                  {isPremiumClient ? 'Enviar solicitud' : 'Contactar a cuidador'}
                </Button>

              </div>
            )}

            <ServiceDatesSelector />
            {/* Location 
            {provider.latitude && provider.longitude && isLoaded && !loadError && (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="provider-map">Map */}
              {provider.latitude && provider.longitude && isLoaded && !loadError && (
  <div className="bg-white rounded-2xl shadow-sm overflow-hidden relative z-0" data-testid="provider-map">
                <div className="p-4 border-b">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#E6202E] fill-[#E6202E]" /> Ubicacion
                  </h3>
                </div>
                <SafeMap lat={provider.latitude} lng={provider.longitude} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <BookingForm 
          provider={provider} 
          onClose={() => setShowBookingForm(false)}
          onSuccess={() => {
            setShowBookingForm(false);
            toast.success('Reserva enviada correctamente');
          }}
        />
      )}
    </div>
  );
}
